// Button — primary action element. One `primary` per view (hierarchy).
// Variants map to semantic tokens; `shape` toggles rounded-rect vs pill.

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import Icon from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  /** Kebab-case Icon name rendered before the label. */
  iconStart?: string;
  /** Kebab-case Icon name rendered after the label. */
  iconEnd?: string;
  /** Show a spinner and block interaction. */
  loading?: boolean;
}

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    iconStart,
    iconEnd,
    loading = false,
    disabled,
    type = 'button',
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const classes = [
    'app-btn',
    `app-btn--${variant}`,
    `app-btn--${size}`,
    shape === 'pill' && 'app-btn--pill',
    loading && 'is-loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = ICON_SIZE[size];

  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading && <span className='app-btn__spinner' aria-hidden='true' />}
      {!loading && iconStart && (
        <Icon name={iconStart} size={iconSize} className='app-btn__icon' aria-hidden='true' />
      )}
      {children && <span className='app-btn__label'>{children}</span>}
      {!loading && iconEnd && (
        <Icon name={iconEnd} size={iconSize} className='app-btn__icon' aria-hidden='true' />
      )}
    </button>
  );
});

export default Button;
